import {
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useId,
  useState,
} from 'react';

import { cn } from '@shared/lib/cn';
import { Spinner } from '@shared/ui/spinner';

import { KNOB_CLASS, switchLook, type SwitchStatus, TRACK_CLASS } from './switch.constant';
import { SwitchSkeleton } from './switch.skeleton.ui';

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'children' | 'value' | 'onChange'
> {
  /** Figma `checked`(제어). 비제어는 `defaultChecked` */
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Figma `status`. init = 스켈레톤, loading = 흐리게 + Spinner, 누를 수 없음 */
  status?: SwitchStatus;
  /** Figma `Label`(+ `Show label`). 없으면 트랙만 그린다 — 그때는 `aria-label` 을 준다 */
  children?: ReactNode;
  /** 바깥 틀에 붙는다 */
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * 켜고 끄는 스위치(`role="switch"`). 트랙 30×16, 손잡이 14.
 * 트랙 · 손잡이 치수는 토큰이 없어 높이(16 = `h-4`)와 정사각 비율로 만든다: 트랙 = 1 + 14 + 14 + 1.
 */
export function Switch({
  checked,
  defaultChecked = false,
  onCheckedChange,
  status = 'default',
  children,
  className,
  disabled = false,
  onClick,
  id,
  ref,
  ...rest
}: SwitchProps) {
  const autoId = useId();
  const [innerChecked, setInnerChecked] = useState(defaultChecked);
  const isOn = checked ?? innerChecked;

  if (status === 'init') return <SwitchSkeleton className={className} />;

  const loading = status === 'loading';
  const look = switchLook(isOn, disabled);
  const buttonId = id ?? autoId;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || loading) return;
    const next = !isOn;
    if (checked === undefined) setInnerChecked(next);
    onCheckedChange?.(next);
  };

  return (
    <span
      data-slot="switch"
      data-status={status}
      className={cn('relative inline-flex items-center gap-2-5 align-middle', className)}
    >
      <button
        ref={ref}
        id={buttonId}
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-busy={loading || undefined}
        {...rest}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          'inline-flex h-4 shrink-0 rounded-full p-px',
          TRACK_CLASS[look],
          isOn && 'flex-row-reverse',
          disabled ? 'cursor-not-allowed' : loading ? 'cursor-progress' : 'cursor-pointer',
          loading && 'opacity-(--opacity-disabled)',
        )}
      >
        <span
          aria-hidden="true"
          className={cn('aspect-square h-full rounded-full', KNOB_CLASS[look])}
        />
        <span aria-hidden="true" className="aspect-square h-full" />
      </button>
      {children ? (
        <label
          htmlFor={buttonId}
          className={cn(
            'typo-body',
            disabled ? 'cursor-not-allowed text-fg-disabled' : 'cursor-pointer text-fg-default',
            loading && 'opacity-(--opacity-disabled)',
          )}
        >
          {children}
        </label>
      ) : null}
      {loading ? (
        <Spinner size="sm" tone="primary" aria-hidden="true" className="absolute top-1 left-3" />
      ) : null}
    </span>
  );
}
